/**
 * Selectors for the teacher training module in Sparx Web which shows a teacher how to use the student
 * experience.
 */
import { createSelector } from 'reselect';
import { getFeatureFlag } from 'phoenix2/state/feature-flags/selectors';
import { TRAINING_MODULE_STUDENT_EXPERIENCE_TRY_SPARX } from 'phoenix2/content/training/training-modules-try-sparx';
import { TRAINING_MODULE_STUDENT_EXPERIENCE } from 'phoenix2/content/training/training-modules';
import { FEATURE_FLAG_TRY_SPARX_SCHOOL } from 'phoenix2/state/feature-flags';
import { TRAINING_KEY, TrainingState } from './reducers';
import { CompletableStep, isCompletable, Module } from './types';

export const getTrainingState = (state: any): TrainingState => state.get(TRAINING_KEY);

export const getTrainingModules = createSelector(getTrainingState, state => state.modules);

export const getTrainingModuleByName = (moduleName: string) =>
    createSelector(getTrainingModules, modules => modules[moduleName]);

/**
 * getTrainingEnabled returns whether the teacher training module in Sparx Web is enabled - i.e. the app is in training
 * mode
 * @param state
 */
export const getTrainingEnabled = (state: any): boolean =>
    getTrainingState(state) ? getTrainingState(state).currentModule !== undefined : false;

/**
 * getCurrentModule return the currently selected module, or undefined if the
 * user is not in training mode.
 * @param state
 */
export const getCurrentModule = (state: any): Module | undefined => {
    const currentModule = getTrainingState(state).currentModule;
    return currentModule && getTrainingState(state).modules[currentModule];
};

export const getTrainingModeSteps = createSelector(getCurrentModule, m => (m ? m.steps : []));

/**
 * getCompletableTrainingSteps returns the completable training steps in the
 * currently selected training module.
 */
export const getCompletableTrainingSteps = createSelector(getTrainingModeSteps, steps =>
    steps.filter((step): step is CompletableStep => isCompletable(step)),
);

/**
 * getTrainingModeCurrentStep returns the currently selected step, or undefined
 * if there is no step selected.
 */
export const getTrainingModeCurrentStep = createSelector(
    getTrainingState,
    getTrainingModeSteps,
    (state, steps) => (state.currentStep !== undefined ? steps[state.currentStep] : undefined),
);

/**
 * getNextIncompleteTrainingStepIndex returns the index of the next training step after the one
 * provided that is not complete. If currentStepIndex is undefined then return undefined. If there
 * are no incomplete steps after the one provided then instead return the first incomplete step. If
 * there is no such step then return undefined.
 */
export const getNextIncompleteTrainingStepIndex = (currentStepIndex: number | undefined) =>
    createSelector(getCompletableTrainingSteps, steps => {
        if (currentStepIndex === undefined) {
            return undefined;
        }
        // Return the index of the first incomplete step after the index provided
        for (let i = currentStepIndex + 1; i < steps.length; i++) {
            if (!steps[i].isComplete) {
                return i;
            }
        }
        // If there are no incomplete steps after the one provided then return the index of the
        // first incomplete step
        const incompleteStepIndex = steps.findIndex(s => !s.isComplete);
        return incompleteStepIndex !== -1 ? incompleteStepIndex : undefined;
    });

export const getWatchingHelpVideo = (state: any): boolean =>
    getTrainingState(state).watchingHelpVideo;

// Users will either get the default training modules or Try Sparx training modules if they are in a
// Try Sparx school.
export const getStudentExperienceTrainingModuleName = (state: any) => {
    const isTrySparxSchool = getFeatureFlag(state, FEATURE_FLAG_TRY_SPARX_SCHOOL);
    return isTrySparxSchool
        ? TRAINING_MODULE_STUDENT_EXPERIENCE_TRY_SPARX.name
        : TRAINING_MODULE_STUDENT_EXPERIENCE.name;
};
